"use client";

import { Component, type ErrorInfo, type ReactNode } from "react";

interface SectionErrorBoundaryProps {
  children: ReactNode;
  label?: string;
}

interface SectionErrorBoundaryState {
  hasError: boolean;
}

export class SectionErrorBoundary extends Component<
  SectionErrorBoundaryProps,
  SectionErrorBoundaryState
> {
  override state: SectionErrorBoundaryState = {
    hasError: false,
  };

  static getDerivedStateFromError(): SectionErrorBoundaryState {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(
      `[SectionErrorBoundary] ${this.props.label ?? "Section"}`,
      error,
      info,
    );
  }

  private handleRetry = () => {
    this.setState({ hasError: false });
  };

  override render() {
    if (this.state.hasError) {
      return (
        <div className="mx-auto my-12 max-w-4xl rounded-2xl border border-red-200 bg-white p-10 text-center shadow-xl shadow-red-500/10">
          <h2 className="font-display text-2xl font-semibold text-red-600">
            Что-то пошло не так
          </h2>
          <p className="text-luxury-stone mt-3 text-base">
            {this.props.label
              ? `Не удалось загрузить блок «${this.props.label}».`
              : "Не удалось загрузить этот блок."}
          </p>
          <p className="text-luxury-stone/70 mt-2 text-sm">
            Мы уже получили уведомление и разбираемся с проблемой. Попробуйте
            обновить блок.
          </p>
          <button
            type="button"
            onClick={this.handleRetry}
            className="bg-luxury-green text-luxury-dark hover:bg-luxury-green-light mt-6 inline-flex items-center justify-center rounded-md px-6 py-3 text-sm font-semibold tracking-wider uppercase transition"
          >
            Повторить загрузку
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
